import { PropertyHelper } from "@/helpers/property.helper";
import { SharingHelper } from "@/helpers/sharing.helper";
import { UserHelper } from "@/helpers/user.helper";
import { Utils } from "@/helpers/utils";
import { AlertService } from "@/services/alert.service";
import { EmailWithTLDValidator } from "@/validators/EmailWithTLDValidator";
import { UserNameValidator } from "@/validators/UserNameValidator";
import { NgFor, NgIf, NgTemplateOutlet, formatDate } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckbox, MatCheckboxModule } from "@angular/material/checkbox";
import { MatOptionModule } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import {
  ErrorBusinessCode,
  RealProperty,
  Sharing,
  SharingCreate,
  SharingDataCategory,
  SharingScope,
  SharingService,
  SharingVisitModeType,
  UserRole,
  UsersService,
} from "@eemo/api-client";
import { MdbPopoverModule } from "mdb-angular-ui-kit/popover";
import { Subscription } from "rxjs";
import { InputNullValueDirective } from "../../extensions/inputNullValueDirective";
import { InputTrimDirective } from "../../extensions/inputTrimDirective";
import { InfoDialogComponent } from "../info-dialog/info-dialog.component";
import { SelectComponent } from "../ui-forms/select/select.component";

import { TooltipDirective } from "@/directives/tooltip/tooltip.directive";
import { ActionService } from "@/services/action.service";
import { AppConfigService } from "@/services/app-config.service";
import { ModalBaseComponent } from "../base/modal-base/modal-base.component";
import { InputComponent } from "../ui-forms/input/input.component";

export class ShareDialogData {
  props?: RealProperty[];
  share?: Sharing;
  edition = false;
  targetUserEmail?: string;
}

@Component({
  selector: "app-share-dialog",
  templateUrl: "./share-dialog.component.html",
  styleUrls: ["./share-dialog.component.scss"],
  standalone: true,
  imports: [
    ModalBaseComponent,
    NgTemplateOutlet,
    MatDialogModule,
    InputComponent,
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    TooltipDirective,
    MatCheckboxModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    InputTrimDirective,
    SelectComponent,
    MdbPopoverModule,
    MatButtonModule,
    MatRadioModule,
    MatSelectModule,
    NgFor,
    MatOptionModule,
    InputNullValueDirective,
    MatDatepickerModule,
  ],
})
export class ShareDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("checkAllData", { static: false })
  private checkAllData: MatCheckbox;
  @ViewChild("checkSelectData", { static: false })
  private checkSelectData: MatCheckbox;
  @ViewChild("checkNoEndDate") private checkNoEndDate: MatCheckbox;
  @ViewChild("container") container: ElementRef<HTMLDivElement>;

  isBrowserSupportsDateInput = Utils.isBrowserSupportsDateInput;

  shareSub = new Subscription();
  visitModeSub = new Subscription();
  targetUserSub = new Subscription();
  targetUserEmailSub = new Subscription();
  targetUserCheckSub = new Subscription();
  userPrefsSub = new Subscription();

  shareForm: UntypedFormGroup;
  dialogTitle: string;
  formSubmitting = false;
  showAdvancedOptions = false;
  sharingCategories: SharingDataCategory[];
  sharingCategoryNames: { [key: string]: string } = {};
  canCreateTargetUserAccount = false;
  canAddVisitMode = false;
  defaultVisitModeFull = true;
  visitModeAvaiable = false;
  visitModeUnavailableMessage: string[] = [];
  targetUserHasAccount = false;

  emailTargetLabel = "targetUserEmail";
  okMessage = "";

  createRoleList = [
    UserRole.INDIVIDUAL,
    UserRole.PROFESSIONAL,
    UserRole.REAL_ESTATE,
    UserRole.NOTARY,
  ];

  autoCompletionType: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ShareDialogData,
    private shareService: SharingService,
    private alertService: AlertService,
    private fb: UntypedFormBuilder,
    public sharingHelper: SharingHelper,
    public dialogRef: MatDialogRef<ShareDialogComponent>,
    @Inject(LOCALE_ID) public locale: string,
    private usersService: UsersService,
    private userHelper: UserHelper,
    private dialog: MatDialog,
    private propHelper: PropertyHelper,
    public actionService: ActionService,
    private cdref: ChangeDetectorRef,
    private appConfigService: AppConfigService
  ) {
    this.autoCompletionType = this.userHelper.isAdmin() ? "users" : "contacts";
    if (this.data.edition) {
      this.dialogTitle = "Modifier le partage";
    } else {
      const titleDetail =
        this.data.props.length === 1
          ? this.data.props[0].name
          : this.data.props.length + " biens";
      this.dialogTitle = "Partager " + titleDetail;
    }
  }

  get form_scope() {
    return this.shareForm.get("scope") as FormControl<SharingScope>;
  }

  get form_allowedData() {
    return this.shareForm.get("allowedData") as FormControl<
      SharingDataCategory[]
    >;
  }

  get form_duration() {
    return this.shareForm.get("duration") as FormControl<string>;
  }

  get form_endDate() {
    return this.shareForm.get("endDate") as FormControl<string>;
  }

  get form_createTargetUserAccount() {
    return this.shareForm.get(
      "createTargetUserAccount"
    ) as FormControl<boolean>;
  }

  get form_targetUserRole() {
    return this.shareForm.get("targetUserRole") as FormControl<UserRole>;
  }

  get form_targetUserEmail() {
    return this.shareForm.get("targetUserEmail") as FormControl<string>;
  }

  get form_targetUserName() {
    return this.shareForm.get("targetUserName") as FormControl<string>;
  }

  get form_targetUserFirstName() {
    return this.shareForm.get("targetUserFirstName") as FormControl<string>;
  }

  get form_message() {
    return this.shareForm.get("message") as FormControl<string>;
  }

  get form_visitMode() {
    return this.shareForm.get("visitMode") as FormControl<boolean>;
  }

  get form_visitModeFull() {
    return this.shareForm.get("visitModeFull") as FormControl<boolean>;
  }

  get form_createAnotherShare() {
    return this.shareForm.get("createAnotherShare") as FormControl<boolean>;
  }

  ngOnInit() {
    this.userPrefsSub = this.appConfigService.userPreferencesObs.subscribe(
      (preferences) => {
        if (preferences) {
          this.defaultVisitModeFull =
            preferences.defaultVisitModeType === SharingVisitModeType.FULL;
        }
      }
    );
    this.canCreateTargetUserAccount =
      this.userHelper.isAdmin() || this.userHelper.isRealEstate();
    this.canAddVisitMode =
      this.userHelper.isAdmin() || this.userHelper.isRealEstate();

    this.shareForm = this.fb.group({
      scope: [SharingScope.VIEWING, Validators.required],
      allowedData: [null],
      duration: [
        { value: "1MONTH", disabled: this.data.edition },
        Validators.required,
      ],
      endDate: [
        {
          value: null,
          disabled: !this.data.edition || !this.data.share.endDate,
        },
      ],
      targetUserEmail: [
        this.data.targetUserEmail,
        [Validators.required, EmailWithTLDValidator],
      ],
      createTargetUserAccount: [false],
      targetUserRole: [UserRole.INDIVIDUAL],
      targetUserName: [null, [Validators.maxLength(50), UserNameValidator]],
      targetUserFirstName: [
        null,
        [Validators.maxLength(50), UserNameValidator],
      ],
      message: [
        { value: null, disabled: this.data.edition },
        Validators.maxLength(160),
      ],
      visitMode: [this.canAddVisitMode ? true : false],
      visitModeFull: [this.defaultVisitModeFull],
      createAnotherShare: [false],
    });

    this.targetUserEmailSub = this.form_targetUserEmail.valueChanges.subscribe(
      (value) => {
        if (this.form_targetUserEmail.valid) {
          this.targetUserCheckSub = this.usersService
            .checkUserDirectoryEmail(this.form_targetUserEmail.value)
            .subscribe((v) => {
              this.targetUserHasAccount = v;
            });
        }
      }
    );
    this.targetUserSub =
      this.form_createTargetUserAccount.valueChanges.subscribe((value) => {
        if (value) {
          this.form_targetUserRole.addValidators(Validators.required);
          this.form_targetUserName.addValidators(Validators.required);
          this.form_targetUserFirstName.addValidators(Validators.required);
        } else {
          this.form_targetUserRole.removeValidators(Validators.required);
          this.form_targetUserName.removeValidators(Validators.required);
          this.form_targetUserFirstName.removeValidators(Validators.required);
        }
        this.form_targetUserRole.updateValueAndValidity();
        this.form_targetUserName.updateValueAndValidity();
        this.form_targetUserFirstName.updateValueAndValidity();
      });

    if (this.data.edition) {
      this.form_targetUserEmail.disable();
    }

    this.sharingCategories = this.sharingHelper.getSharingDataCategories();
    const mainPropType = this.data.edition
      ? this.data.share.property.type
      : this.data.props[0].type;
    this.sharingCategories.forEach(
      (c) =>
        (this.sharingCategoryNames[c] =
          this.sharingHelper.getSharingDataCategoryName(c, mainPropType))
    );

    this.allDataChecked(null);
    if (this.data.share) {
      // update
      this.showAdvancedOptions = true;
      this.shareForm.patchValue(this.data.share);
      if (this.data.share.allowedData.length > 0) {
        this.form_allowedData.setValue(this.data.share.allowedData);
      }
      this.form_visitMode.disable();
    } else {
      //creation: check if visit mode can be enabled
      if (this.canAddVisitMode) {
        this.visitModeAvaiable = true;
        this.data.props.forEach((p) => {
          this.visitModeSub = this.shareService
            .getRealPropertySharingVisitStatus(p.uid)
            .subscribe((s) => {
              if (!s.isReady) {
                this.visitModeAvaiable = false;
                this.form_visitMode.setValue(false);
                this.form_visitMode.disable();
                s.missingDocs.forEach((d) => {
                  this.visitModeUnavailableMessage.push(
                    'Document "' +
                      this.propHelper.getDocumentIdentifierName(d) +
                      '" non présent pour le bien "' +
                      p.name +
                      '"'
                  );
                });
              }
            });
        });
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.data.share) {
      this.scopeChanged({ value: this.data.share.scope, initial: true });
      if (this.data.share.allowedData.length > 0) {
        this.selectDataChecked(null);
      }
    }
  }

  ngOnDestroy(): void {
    this.shareSub.unsubscribe();
    this.visitModeSub.unsubscribe();
    this.targetUserSub.unsubscribe();
    this.targetUserEmailSub.unsubscribe();
    this.targetUserCheckSub.unsubscribe();
    this.userPrefsSub.unsubscribe();
  }

  isScopeViewing() {
    return this.form_scope.value === SharingScope.VIEWING;
  }
  scopeChanged(event: any) {
    const dataDisabled = event.value === SharingScope.DELEGATION;
    this.checkAllData.disabled = dataDisabled;
    this.checkSelectData.disabled = dataDisabled;
    this.allDataChecked(event);
    this.cdref.detectChanges();
  }

  allDataChecked(event: any) {
    if (this.checkAllData) {
      this.checkAllData.checked = true;
    }
    if (this.checkSelectData) {
      this.checkSelectData.checked = false;
    }
    if (event && !event.initial) {
      // we don't reset the form value at the initial loading
      this.form_allowedData.setValue(null);
    }
    this.form_allowedData.disable();
  }

  selectDataChecked(event: any) {
    if (this.checkAllData) {
      this.checkAllData.checked = false;
    }
    if (this.checkSelectData) {
      this.checkSelectData.checked = true;
    }
    if (
      !this.form_allowedData.value ||
      this.form_allowedData.value.length === 0
    ) {
      this.form_allowedData.setValue(
        this.sharingHelper.getSharingDefaultDataCategory()
      );
    }
    this.form_allowedData.enable();
    this.cdref.detectChanges();
  }

  noDateChanged(event: any) {
    if (event.checked) {
      this.form_endDate.disable();
    } else {
      this.form_endDate.enable();
    }
  }

  onSubmit() {
    // validate fields
    // parse the date to format date send by mat datepicker (browser with support of input type="date" doesn't need it)
    const date = new Date(this.form_endDate.value);
    this.form_endDate.setValue(formatDate(date, "yyyy-MM-dd", this.locale));

    const endDate =
      this.checkNoEndDate &&
      !this.checkNoEndDate.checked &&
      this.form_endDate.value
        ? new Date(this.form_endDate.value)
        : null;
    if (endDate && !Utils.isDateInTheFuture(endDate)) {
      this.form_endDate.setErrors({ futureDate: true });
      this.form_endDate.markAsTouched();
    }

    if (this.shareForm.valid) {
      this.formSubmitting = true;

      const scope: SharingScope = this.form_scope.value;
      const allowedData: SharingDataCategory[] = [];
      if (
        scope !== SharingScope.DELEGATION &&
        this.checkSelectData?.checked &&
        Array.isArray(this.form_allowedData.value)
      ) {
        this.form_allowedData.value.forEach((element) => {
          allowedData.push(SharingDataCategory[element]);
        });
      }
      if (this.data.edition) {
        this.data.share.scope = scope;
        this.data.share.allowedData = allowedData;
        this.data.share.endDate = endDate
          ? formatDate(endDate, "yyyy-MM-dd", this.locale)
          : null;
        this.shareSub = this.shareService
          .updateSharing(this.data.share.uid, this.data.share)
          .subscribe({
            next: () => {
              this.dialogRef.close(true);
              this.alertService.success("Modification du partage effectuée");
            },
            error: () => {
              this.dialogRef.close(false);
            },
          });
      } else {
        const shareCreate: SharingCreate = {
          targetProperties: this.data.props.map((p) => p.uid),
          scope,
          allowedData,
          endDate: this.sharingHelper.calculateEndDate(
            this.form_duration.value
          ),
          targetUserEmail: this.form_targetUserEmail.value,
          message: this.form_message.value,
          createTargetUserAccount: this.form_createTargetUserAccount.value,
          targetUserRole: this.form_targetUserRole.value,
          targetUserName: this.form_targetUserName.value,
          targetUserFirstName: this.form_targetUserFirstName.value,
          visitMode: this.form_visitMode.value,
          visitModeType: this.form_visitModeFull.value
            ? SharingVisitModeType.FULL
            : SharingVisitModeType.SIMPLE,
        };
        this.shareSub = this.shareService
          .addRealPropertiesSharings(shareCreate)
          .subscribe({
            next: (createdId) => {
              if (this.form_createAnotherShare.value) {
                //reset the form target users and keep the dialog open
                this.form_targetUserEmail.setValue(null);
                this.form_targetUserEmail.setErrors(null);
                this.form_targetUserName.setValue(null);
                this.form_targetUserFirstName.setValue(null);
                this.shareForm.markAsPristine();
                this.formSubmitting = false;
                this.targetUserHasAccount = false;
                this.container.nativeElement.scrollTop = 0;
                this.emailTargetLabel = "nextTargetUserEmail";
                this.okMessage = "Création du partage effectuée";
                setTimeout(() => (this.okMessage = ""), 2000);
              } else {
                this.alertService.success(
                  "Création du partage effectuée. Une notification a été envoyée au destinataire."
                );
                this.dialogRef.close(createdId);
              }
            },
            error: (err: HttpErrorResponse) => {
              if (
                err.error &&
                err.error.businessCode ===
                  ErrorBusinessCode.SHARE_TO_COMPANY_MANAGER
              ) {
                this.alertService.warning(
                  "Le destinataire dispose déjà d'un accès au bien en tant que référent de l'agence."
                );
              }
              this.dialogRef.close(false);
            },
          });
      }
    }
  }
  onNoClick(): void {
    this.dialogRef.close(false);
  }

  toogleAdvancedOptions() {
    this.showAdvancedOptions = !this.showAdvancedOptions;
  }

  getOptionsButtonText() {
    return this.showAdvancedOptions ? "Moins d'options" : "Plus d'options";
  }

  showCreateUserInfo() {
    // prevents to open multiple dialog
    const dialogRef = this.dialog.open(InfoDialogComponent, {
      maxWidth: "30rem",
      data: {
        title: "Création du compte du destinataire",
        content:
          "<p>En sélectionnant cette option, le compte de l'utilisateur va être créé automatiquement s'il n'existe pas.</p>" +
          "<p>Il recevra un email avec son identifiant et son mot de passe.</p>" +
          "<p class=\"mt-2\">Si cette option n'est pas sélectionnée, il recevra un mail lui indiquant qu'un bien lui a été partagé et sera invité à créer son compte.</p>",
      },
    });
  }

  showVisitModeInfo() {
    // prevents to open multiple dialog
    this.dialog.open(InfoDialogComponent, {
      maxWidth: "30rem",
      data: {
        title: "Activation de l'option visite",
        content:
          "<p>En sélectionnant cette option, le destinataire devra valider un bon de visite pour pouvoir accéder aux données du bien.</p>" +
          '<p class="mt-2">Vous serez notifié par mail de sa validation.</p>',
      },
    });
  }

  showVisitModeTypeInfo() {
    // prevents to open multiple dialog
    this.dialog.open(InfoDialogComponent, {
      maxWidth: "30rem",
      data: {
        title: "Validation de l'ERP",
        content:
          '<p>En sélectionnant cette option, le destinataire devra, en plus du bon de visite, visualiser et valider le document "État des risques et pollutions" pour pouvoir accéder aux données du bien.</p>' +
          '<p class="mt-2">Si elle n\'est pas sélectionnée, le document "État des risques et pollutions" sera tout de même joint au mail de validation pour consultation.</p>',
      },
    });
  }
}
